// Honest 24 h / 96-slot cycle-state derivation for the quiet ribbon on Konfiguraatio, Datan
// laatu, Talotekniikan toimivuus, Sisäilmaolosuhteet, and Energiatehokkuus (stories #1612 /
// #1768). Sibling pure-helper to kb-run-timing.ts: no React, no DOM, no colour value — this
// returns states, a view maps them to tone. "Not evaluated" always comes from the
// evaluation-run marker, never from an absent Finding (docs/spec/knowledge-base.md
// § Evaluation-run marker).
import { KB_TICK_INTERVAL_MS, isSameLocalDate } from "./kb-run-timing";
import type { KBEvaluationRun, KBTickSummary } from "@/lib/api/agent";
import type { Finding } from "@/lib/api/building";
import type { LensFindingFilter } from "./lens-topic-metrics";

/** The ribbon always renders this many 15-min slots, spanning the trailing 24 h. */
export const CYCLE_SLOT_COUNT = (24 * 60) / 15; // 96

export type CycleSlotState =
  "not-evaluated" | "no-sweep" | "clean" | "findings" | "error";

export interface CycleSlotTick {
  tick_id: string;
  started_at: string;
  finished_at: string;
  equipment_evaluated: number;
  zones_evaluated: number;
  data_points_written: number;
  behaviours_failing: number;
  errors: number;
}

export interface CycleSlot {
  start: Date;
  end: Date;
  state: CycleSlotState;
  /** Headline of the sweep that coloured this slot, or null when there was no recorded run. */
  tick: CycleSlotTick | null;
}

type EvaluationRunWindow = Pick<
  KBEvaluationRun,
  "first_evaluated_at" | "last_evaluated_at"
>;

type TickWindow = Pick<
  KBTickSummary,
  "started_at" | "finished_at" | "behaviours_failing" | "errors"
> &
  Partial<
    Pick<
      KBTickSummary,
      | "tick_id"
      | "equipment_evaluated"
      | "zones_evaluated"
      | "data_points_written"
    >
  >;

type SweepSeverity = "clean" | "findings" | "error";

const SEVERITY_RANK: Record<SweepSeverity, number> = {
  clean: 0,
  findings: 1,
  error: 2,
};

function toSlotTick(tick: TickWindow | null): CycleSlotTick | null {
  if (!tick?.tick_id) return null;
  return {
    tick_id: tick.tick_id,
    started_at: tick.started_at,
    finished_at: tick.finished_at,
    equipment_evaluated: tick.equipment_evaluated ?? 0,
    zones_evaluated: tick.zones_evaluated ?? 0,
    data_points_written: tick.data_points_written ?? 0,
    behaviours_failing: tick.behaviours_failing,
    errors: tick.errors,
  };
}

function sweepSeverity(tick: TickWindow): SweepSeverity {
  if (tick.errors > 0) return "error";
  if (tick.behaviours_failing > 0) return "findings";
  return "clean";
}

function overlaps(
  aStartMs: number,
  aEndMs: number,
  bStartMs: number,
  bEndMs: number,
): boolean {
  return aStartMs < bEndMs && aEndMs > bStartMs;
}

/** Parses an ISO timestamp to epoch ms, or null for a missing/unparseable value. */
function parseMsOrNull(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Derives exactly `CYCLE_SLOT_COUNT` slots for the trailing 24 h ending at `nowMs`, oldest first.
 * A sliding window (not wall-clock aligned) so it never depends on when the KB scheduler started.
 *
 * Per slot: a tick overlapping the window decides `clean` / `findings` / `error` (most severe
 * wins when several overlap). Otherwise the window falls back to the evaluation-run marker:
 * outside `[first_evaluated_at, last_evaluated_at]`, or no marker at all, is `not-evaluated`
 * (never-started or stopped agent); inside the range with no overlapping tick is `no-sweep`
 * (missed cycle). No slot is ever fabricated as `clean` from an absent sweep.
 */
export function deriveCycleSlots(
  evaluationRun: EvaluationRunWindow,
  ticks: TickWindow[],
  nowMs: number = Date.now(),
): CycleSlot[] {
  const firstEvaluatedMs = parseMsOrNull(evaluationRun.first_evaluated_at);
  const lastEvaluatedMs = parseMsOrNull(evaluationRun.last_evaluated_at);

  const windowStartMs = nowMs - CYCLE_SLOT_COUNT * KB_TICK_INTERVAL_MS;

  const slots: CycleSlot[] = [];
  for (let i = 0; i < CYCLE_SLOT_COUNT; i++) {
    const slotStartMs = windowStartMs + i * KB_TICK_INTERVAL_MS;
    const slotEndMs = slotStartMs + KB_TICK_INTERVAL_MS;

    let severity: SweepSeverity | null = null;
    let winningTick: TickWindow | null = null;
    for (const tick of ticks) {
      const tickStartMs = parseMsOrNull(tick.started_at);
      const tickEndMs = parseMsOrNull(tick.finished_at);
      if (tickStartMs === null || tickEndMs === null) continue;
      if (!overlaps(slotStartMs, slotEndMs, tickStartMs, tickEndMs)) continue;
      const tickSeverity = sweepSeverity(tick);
      if (
        severity === null ||
        SEVERITY_RANK[tickSeverity] > SEVERITY_RANK[severity]
      ) {
        severity = tickSeverity;
        winningTick = tick;
      }
    }

    const insideEvaluatedRange =
      firstEvaluatedMs !== null &&
      lastEvaluatedMs !== null &&
      overlaps(slotStartMs, slotEndMs, firstEvaluatedMs, lastEvaluatedMs);

    const state: CycleSlotState =
      severity ?? (insideEvaluatedRange ? "no-sweep" : "not-evaluated");

    slots.push({
      start: new Date(slotStartMs),
      end: new Date(slotEndMs),
      state,
      tick: toSlotTick(winningTick),
    });
  }

  return slots;
}

/**
 * Page Havainnot whose open interval overlaps a slot's `[start, end)`. A long-open
 * finding still counts on later sweeps. Used both for the amber dialog list and for
 * per-lens strip colouring.
 */
export function findingsOpenDuringSlot(
  findings: Finding[],
  slot: Pick<CycleSlot, "start" | "end">,
  filter: LensFindingFilter,
): Finding[] {
  const startMs = slot.start.getTime();
  const endMs = slot.end.getTime();
  return findings.filter((finding) => {
    if (filter.category !== undefined && finding.category !== filter.category) {
      return false;
    }
    if (
      filter.sourceType !== undefined &&
      finding.source_type !== filter.sourceType
    ) {
      return false;
    }
    const openedMs = Date.parse(finding.opened_at);
    if (Number.isNaN(openedMs)) return false;
    const resolvedMs = finding.resolved_at
      ? Date.parse(finding.resolved_at)
      : Number.POSITIVE_INFINITY;
    if (Number.isNaN(resolvedMs)) return false;
    return openedMs < endMs && resolvedMs > startMs;
  });
}

/**
 * On a per-lens ribbon, amber vs green follows this page's Havainnot. Grey and sweep-error
 * stay on the marker / tick (`not-evaluated`, `no-sweep`, `error`) so an absent Finding never
 * fabricates a clean unknown window.
 */
export function applyLensSlotColouring(
  slots: CycleSlot[],
  findings: Finding[],
  filter: LensFindingFilter,
): CycleSlot[] {
  return slots.map((slot) => {
    if (
      slot.state === "not-evaluated" ||
      slot.state === "no-sweep" ||
      slot.state === "error"
    ) {
      return slot;
    }
    const hits = findingsOpenDuringSlot(findings, slot, filter);
    const state: CycleSlotState = hits.length > 0 ? "findings" : "clean";
    return state === slot.state ? slot : { ...slot, state };
  });
}

/**
 * Index of the first slot whose start falls on a different local calendar day than the
 * first slot — where the ribbon draws its day-break divider. A fixed 24 h window crosses
 * exactly one midnight except when queried at local midnight itself, so this is the actual
 * date-change moment (unlike the leftmost slot, which is just wherever the window happens
 * to start). Null when the whole window stays within one calendar day.
 */
export function dayBreakSlotIndex(slots: CycleSlot[]): number | null {
  if (slots.length === 0) return null;
  const firstDate = slots[0].start;
  for (let i = 1; i < slots.length; i++) {
    if (!isSameLocalDate(firstDate, slots[i].start)) return i;
  }
  return null;
}
